/** Bond Predictions — filters, grouping, formatting */

import { formatAppDate, formatAppDateTime } from "./appTimeFormat";
import {
  fixtureHasFavoriteTeam,
  isBetFromFollowedCreator,
} from "./p2pHelpers";

export const COMMUNITY_FILTERS = [
  { id: "all", label: "All" },
  { id: "free", label: "Free" },
  { id: "premium", label: "Premium" },
  { id: "unlocked", label: "Unlocked" },
  { id: "following", label: "Following" },
  { id: "favorites", label: "Favorites" },
  { id: "today", label: "Today" },
  { id: "tomorrow", label: "Tomorrow" },
  { id: "week", label: "This week" },
];

export const AI_FILTERS = [
  { id: "all", label: "All" },
  { id: "unlocked", label: "Unlocked" },
  { id: "favorites", label: "Favorites" },
  { id: "today", label: "Today" },
  { id: "tomorrow", label: "Tomorrow" },
  { id: "week", label: "This week" },
];

const startOfDay = (d = new Date()) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const dateKeyFromTs = (ts) => {
  if (!ts) return "no-fixture";
  const d = new Date(Number(ts) * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const formatSectionTitle = (dateKey) => {
  if (dateKey === "no-fixture") return "Other picks";
  const today = dateKeyFromTs(Math.floor(Date.now() / 1000));
  const tomorrowDate = startOfDay();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrow = dateKeyFromTs(Math.floor(tomorrowDate.getTime() / 1000));
  if (dateKey === today) return "Today";
  if (dateKey === tomorrow) return "Tomorrow";
  const [y, m, d] = dateKey.split("-").map(Number);
  return formatAppDate(new Date(y, m - 1, d));
};

export const formatKickoff = (ts) => {
  if (!ts) return "TBD";
  return formatAppDateTime(Number(ts) * 1000);
};

export const formatCountdown = (ts) => {
  if (!ts) return "";
  const diff = Number(ts) * 1000 - Date.now();
  if (diff <= 0) return "Started";
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h >= 48) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export const getFixtureTs = (item, mode) => {
  if (mode === "ai") return item?.fixture?.timestamp || item?.fixtureData?.timestamp;
  return item?.fixture?.timestamp;
};

export const groupByFixtureDate = (items, mode) => {
  const groups = {};
  items.forEach((item) => {
    const key = dateKeyFromTs(getFixtureTs(item, mode));
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  return Object.keys(groups)
    .sort((a, b) => {
      if (a === "no-fixture") return 1;
      if (b === "no-fixture") return -1;
      return a.localeCompare(b);
    })
    .map((dateKey) => ({
      dateKey,
      title: formatSectionTitle(dateKey),
      items: groups[dateKey],
    }));
};

export const extractLeagues = (items, mode) => {
  const map = new Map();
  items.forEach((item) => {
    const league =
      mode === "ai"
        ? item?.league || item?.fixture?.league
        : item?.fixture?.league;
    const id = league?._id || league?.id;
    if (id && league?.name) {
      map.set(String(id), { id: String(id), name: league.name, logo: league.logo });
    }
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const sameDay = (ts, offsetDays = 0) => {
  if (!ts) return false;
  const target = startOfDay();
  target.setDate(target.getDate() + offsetDays);
  const d = new Date(Number(ts) * 1000);
  return (
    d.getFullYear() === target.getFullYear() &&
    d.getMonth() === target.getMonth() &&
    d.getDate() === target.getDate()
  );
};

const inThisWeek = (ts) => {
  if (!ts) return false;
  const now = Date.now();
  const end = new Date(startOfDay());
  end.setDate(end.getDate() + (7 - end.getDay()));
  end.setHours(23, 59, 59, 999);
  const t = Number(ts) * 1000;
  return t > now && t <= end.getTime();
};

export const filterCommunity = (items, filterId, userId, leagueId, ctx = {}) => {
  let list = [...items];
  const uid = userId?.toString();
  const { favoriteTeam, followingIds = [] } = ctx;

  switch (filterId) {
    case "free":
      list = list.filter((p) => !p.isPremium);
      break;
    case "premium":
      list = list.filter((p) => p.isPremium);
      break;
    case "unlocked":
      list = list.filter((p) => {
        const mine = p.user?._id?.toString() === uid;
        const unlocked = p.unlockedBy?.some((id) => id?.toString() === uid);
        return !p.isPremium || mine || unlocked;
      });
      break;
    case "following":
      list = list.filter((p) =>
        isBetFromFollowedCreator({ user: p.user }, followingIds)
      );
      break;
    case "favorites":
      list = list.filter((p) => fixtureHasFavoriteTeam(p.fixture, favoriteTeam));
      break;
    case "today":
      list = list.filter((p) => sameDay(p.fixture?.timestamp, 0));
      break;
    case "tomorrow":
      list = list.filter((p) => sameDay(p.fixture?.timestamp, 1));
      break;
    case "week":
      list = list.filter((p) => inThisWeek(p.fixture?.timestamp));
      break;
    default:
      break;
  }

  if (leagueId) {
    list = list.filter((p) => String(p.fixture?.league?._id) === leagueId);
  }
  return list;
};

export const filterAI = (items, filterId, leagueId, favoriteTeam) => {
  let list = [...items];

  switch (filterId) {
    case "unlocked":
      list = list.filter((p) => p.isUnlocked);
      break;
    case "favorites":
      list = list.filter((p) =>
        fixtureHasFavoriteTeam(
          {
            ...(p.fixture || {}),
            homeTeam: p.homeTeam,
            awayTeam: p.awayTeam,
            teams: p.fixture?.teams || { home: p.homeTeam, away: p.awayTeam },
          },
          favoriteTeam
        )
      );
      break;
    case "today":
      list = list.filter((p) => sameDay(getFixtureTs(p, "ai"), 0));
      break;
    case "tomorrow":
      list = list.filter((p) => sameDay(getFixtureTs(p, "ai"), 1));
      break;
    case "week":
      list = list.filter((p) => inThisWeek(getFixtureTs(p, "ai")));
      break;
    default:
      break;
  }

  if (leagueId) {
    list = list.filter((p) => String(p.league?._id || p.league?.id) === leagueId);
  }
  return list;
};

/** True when the viewer may read a community tip's picks / analysis. */
export const canViewCommunity = (prediction, userId) => {
  if (!prediction?.isPremium) return true;
  // Owner always sees their own tip without paying.
  if (!userId) return false;
  const uid = String(userId);
  const ownerId = prediction.user?._id ?? prediction.user?.id ?? prediction.user;
  if (ownerId != null && String(ownerId) === uid) return true;
  // Paid unlock for this viewer (socket / local mark).
  if (prediction.isUnlockedByUser) return true;
  return Boolean(
    prediction.unlockedBy?.some((id) => String(id?._id ?? id) === uid)
  );
};

export const isOwnCommunityTip = (prediction, userId) => {
  if (!userId || !prediction) return false;
  const ownerId = prediction.user?._id ?? prediction.user?.id ?? prediction.user;
  return ownerId != null && String(ownerId) === String(userId);
};

export const topOutcome = (prediction) => {
  const outcomes = prediction?.outcomes || [];
  if (!outcomes.length) return null;
  return [...outcomes].sort((a, b) => (b.confidence || 0) - (a.confidence || 0))[0];
};

export const aiAdviceLabel = (prediction) => {
  const advice = prediction?.predictions?.advice || prediction?.advice;
  if (!advice) return "Analysis ready";
  return String(advice).replace(/_/g, " ");
};

/** API percent values arrive as strings like "45" or "45%". */
export const parsePercentValue = (value) => {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.min(100, Math.max(0, value));
  }
  const cleaned = String(value).replace(/%/g, "").trim();
  const n = parseFloat(cleaned);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
};

export const normalizePercent = (percent = {}) => ({
  home: parsePercentValue(percent.home),
  draw: parsePercentValue(percent.draw),
  away: parsePercentValue(percent.away),
});

export const aiConfidence = (prediction) => {
  const p = normalizePercent(prediction?.predictions?.percent || prediction?.percent || {});
  return Math.max(p.home, p.draw, p.away);
};

export const confidenceTone = (confidence) => {
  const c = parseInt(confidence, 10) || 0;
  if (c >= 75) return "high";
  if (c <= 35) return "low";
  return "mid";
};

export const isMatchStarted = (fixture) => {
  if (!fixture) return false;
  const short = fixture.status?.short;
  if (short && short !== "NS") return true;
  if (fixture.timestamp && Number(fixture.timestamp) * 1000 <= Date.now()) return true;
  return false;
};

export const fixtureStatusTone = (fixture) => {
  const short = fixture?.status?.short || "NS";
  const started = isMatchStarted(fixture);
  const isLive = started && !["FT", "AET", "PEN"].includes(short);
  const isFinished = ["FT", "AET", "PEN"].includes(short);
  if (isLive) return { label: short || "LIVE", tone: "live" };
  if (isFinished) return { label: short, tone: "finished" };
  return { label: short, tone: "upcoming" };
};

export const formatTimeSince = (date) => {
  if (!date) return "";
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatAppDate(date);
};

export const AI_UNLOCK_COST = 250;
export const AI_FREE_UNLOCKS_PER_WEEK = 2;

export const getAIUnlockPricing = (user) => {
  const used = Number(user?.weekPredictionCount) || 0;
  const remaining = Math.max(0, AI_FREE_UNLOCKS_PER_WEEK - used);
  if (remaining > 0) {
    return {
      cost: AI_UNLOCK_COST,
      isFree: true,
      remaining,
      label: "Free unlock",
      paidLabel: `${AI_UNLOCK_COST} COIN`,
    };
  }
  return {
    cost: AI_UNLOCK_COST,
    isFree: false,
    remaining: 0,
    label: `${AI_UNLOCK_COST} COIN`,
    paidLabel: `${AI_UNLOCK_COST} COIN`,
  };
};

export const formatCountdownDetailed = (ts) => {
  if (!ts) return "";
  const diff = Number(ts) * 1000 - Date.now();
  if (diff <= 0) return "";
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  return parts.join(" ") || "<1m";
};
